import React, { useCallback, useMemo, useEffect, useState, useRef } from 'react';
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  useReactFlow,
  useUpdateNodeInternals,
  Node,
  Edge,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { AutomationNodeType, AutomationActionNodeSubType } from '@frontend/app/types/globalTypes';
import { GetNodeOptions_getNodeOptions_actionOptions } from '@frontend/app/queries/types/GetNodeOptions';
import { logger } from '@common';
import { AutomationTriggerV2, AutomationNodeV2, FlowNodeData, NodeTemplate, AutomationEditorActions } from '../types';
import { generateNodeId } from '../utils';
import { FLOW_COLORS } from '../constants';
import { findNodeById, cloneNode } from '../utils/automationTreeUtils';
import { TriggerNodeV2, DelayNodeV2, ConditionNodeV2, ActionNodeV2, BranchNodeV2, PlaceholderNodeV2, DashedPlaceholderNode } from '../NodeComponents';
import { CustomEdge } from '../EdgeComponents';
// Removed getDefaultEdgeLabel import - no longer using default labels

// Simple End Node Component with handle for connection
const EndNodeV2: React.FC<{ data: any }> = () => (
  <div className="relative">
    <Handle
      type="target"
      position={Position.Top}
      isConnectable={false}
      className="w-0 h-0 opacity-0"
      style={{ left: '50%', transform: 'translateX(-50%)' }} // Center the handle on the node
    />
    <div className="bg-grey-2 text-grey-6 rounded-lg px-2 py-0.5 text-xs font-medium leading-5">
      End
    </div>
  </div>
);

// Node types for ReactFlow - use V2 components
const nodeTypes = {
  TRIGGER: TriggerNodeV2,
  [AutomationNodeType.DELAY]: DelayNodeV2,
  [AutomationNodeType.CONDITION]: ConditionNodeV2,
  [AutomationNodeType.ACTION]: ActionNodeV2,
  BRANCH: BranchNodeV2,
  END: EndNodeV2, // Add End node type
  PLACEHOLDER: PlaceholderNodeV2, // Add Placeholder node type
  DASHED_PLACEHOLDER: DashedPlaceholderNode, // Add Dashed Placeholder node type

};

// Edge types for ReactFlow - use custom edge with labels
const edgeTypes = {
  custom: CustomEdge,
};

interface FlowCanvasProps {
  trigger: AutomationTriggerV2;
  isEditing: boolean;
  selectedNodeId: string | null;
  onNodeClick: (nodeId: string) => void;
  onUpdateNode: (nodeId: string, updates: Partial<AutomationNodeV2>) => void;
  onAddChild: (parentId: string, template: NodeTemplate, isElseBranch?: boolean, isBranch?: boolean) => void;
  onDeleteNode: (nodeId: string) => void;
  onDeletePath: (nodeId: string) => void;
  actions: AutomationEditorActions;
  automationId?: string;
  templateId?: string;
  actionOptions?: GetNodeOptions_getNodeOptions_actionOptions[];
  programData?: any;
  isStaOffer?: boolean;
}

export const FlowCanvas: React.FC<FlowCanvasProps> = ({
  trigger,
  isEditing,
  selectedNodeId,
  onNodeClick,
  onUpdateNode,
  onAddChild,
  onDeleteNode,
  onDeletePath,
  actions,
  automationId,
  templateId,
  actionOptions,
  programData,
  isStaOffer,
}) => {
  const { fitView } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const hasInitializedView = useRef(false);
  const [nodes, setNodes, onNodesChange] = useNodesState<FlowNodeData>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [edgeUpdateTrigger, setEdgeUpdateTrigger] = useState(0);
  const [showDashedPlaceholder, setShowDashedPlaceholder] = useState<{ nodeId: string; isElseBranch?: boolean; hasOppositeBranch?: boolean; clickedBranch?: 'then' | 'else' } | null>(null);
  // Centralized toolbar state management
  const [activeToolbarNodeId, setActiveToolbarNodeId] = useState<string | null>(null);

  // Node height tracking for consistent 32px spacing
  const [nodeHeights, setNodeHeights] = useState<Map<string, number>>(new Map());
  const [layoutUpdateTrigger, setLayoutUpdateTrigger] = useState(0);

  // Handle edge label changes
  const handleEdgeLabelChange = useCallback((edgeId: string, newLabel: string) => {
    // Parse edge ID to get target node ID
    // Edge IDs can be: "trigger-{nodeId}", "{parentId}-true-{nodeId}", "{parentId}-false-{nodeId}", "{parentId}-{nodeId}"
    let targetNodeId: string | null = null;

    if (edgeId.startsWith('trigger-')) {
      targetNodeId = edgeId.replace('trigger-', '');
    } else if (edgeId.includes('-true-')) {
      targetNodeId = edgeId.split('-true-')[1];
    } else if (edgeId.includes('-false-')) {
      targetNodeId = edgeId.split('-false-')[1];
    } else if (edgeId.includes('-to-end')) {
      // Don't update for end nodes
      return;
    } else {
      // For regular edges like "{parentId}-{nodeId}" where both are UUIDs
      const parts = edgeId.split('-');
      if (parts.length === 10) {
        // Standard UUID format: 8-4-4-4-12 characters, so 5 parts each
        // First UUID: parts[0-4], Second UUID: parts[5-9]
        targetNodeId = parts.slice(5, 10).join('-');
      } else {
        // Fallback to old logic for non-UUID edges
        targetNodeId = parts[parts.length - 1];
      }
    }

    // Update the node with the new edge label
    if (targetNodeId && onUpdateNode) {
      onUpdateNode(targetNodeId, { edgeLabel: newLabel });
      // Force edge update
      setEdgeUpdateTrigger((prev) => prev + 1);
    }
  }, [onUpdateNode]);

  // Handle edge label delete (set to null)
  const handleEdgeLabelDelete = useCallback((edgeId: string) => {
    // Parse edge ID to get target node ID using same logic as handleEdgeLabelChange
    let targetNodeId: string | null = null;

    if (edgeId.startsWith('trigger-')) {
      targetNodeId = edgeId.replace('trigger-', '');
    } else if (edgeId.includes('-true-')) {
      targetNodeId = edgeId.split('-true-')[1];
    } else if (edgeId.includes('-false-')) {
      targetNodeId = edgeId.split('-false-')[1];
    } else if (edgeId.includes('-to-end')) {
      // Don't update for end nodes
      return;
    } else {
      // For regular edges like "{parentId}-{nodeId}"
      const parts = edgeId.split('-');
      targetNodeId = parts[parts.length - 1];
    }

    // Set the edge label to null
    if (targetNodeId && onUpdateNode) {
      onUpdateNode(targetNodeId, { edgeLabel: null });
      // Force edge update
      setEdgeUpdateTrigger((prev) => prev + 1);
    }
  }, [onUpdateNode]);

  // Handle edge duplication (duplicate the target node as a new parallel branch)
  const handleEdgeDuplicate = useCallback((edgeId: string) => {
    // Parse edge ID to get target node ID and parent node ID
    let targetNodeId: string | null = null;
    let parentNodeId: string | null = null;
    let isElseBranch = false; // Track which branch this edge belongs to

    if (edgeId.startsWith('trigger-')) {
      // For trigger to root node edge, the parent is the trigger, target is root
      targetNodeId = edgeId.replace('trigger-', '');
      parentNodeId = trigger.id; // Use trigger as parent
      isElseBranch = false; // Trigger edges are never else branch
    } else if (edgeId.includes('-true-')) {
      const parts = edgeId.split('-true-');
      parentNodeId = parts[0];
      targetNodeId = parts[1];
      isElseBranch = false; // This is a true branch
    } else if (edgeId.includes('-false-')) {
      const parts = edgeId.split('-false-');
      parentNodeId = parts[0];
      targetNodeId = parts[1];
      isElseBranch = true; // This is a false branch
    } else if (edgeId.includes('-to-end')) {
      // Don't duplicate for end nodes
      return;
    } else {
      // For regular edges like "{parentId}-{nodeId}" where both are UUIDs
      const parts = edgeId.split('-');
      if (parts.length === 10) {
        // Standard UUID format: 8-4-4-4-12 characters, so 5 parts each
        // First UUID: parts[0-4], Second UUID: parts[5-9]
        parentNodeId = parts.slice(0, 5).join('-');
        targetNodeId = parts.slice(5, 10).join('-');
      } else {
        // Fallback to old logic for non-UUID edges
        parentNodeId = parts.slice(0, -1).join('-');
        targetNodeId = parts[parts.length - 1];
      }
      isElseBranch = false; // Regular edges default to false
    }

    // Find the target node and its complete subtree
    if (targetNodeId && parentNodeId) {
      const targetNode = findNodeById(trigger.root, targetNodeId);
      if (targetNode) {
        // Clone the entire subtree (node + all its children) with new IDs
        const clonedSubtree = cloneNode(targetNode, true);

        // Add the cloned subtree as a new parallel branch under the same parent
        // Pass the correct isElseBranch value based on which branch was clicked
        if (actions.addNode) {
          actions.addNode(parentNodeId, clonedSubtree, isElseBranch, true); // isBranch: true for parallel branching
        }
      }
    }
  }, [trigger, actions]);

  // Handle node resize events for dynamic spacing
  const handleNodeResize = useCallback((nodeId: string, newHeight: number) => {
    setNodeHeights((prevHeights) => {
      const newHeights = new Map(prevHeights);
      const currentHeight = newHeights.get(nodeId);

      // Only update if height actually changed to avoid unnecessary recalculations
      if (currentHeight !== newHeight) {
        newHeights.set(nodeId, newHeight);

        // Trigger layout recalculation after height update
        setTimeout(() => {
          setLayoutUpdateTrigger((prev) => prev + 1);

          // Update node internals for proper arrow positioning
          updateNodeInternals(nodeId);
        }, 0);
      }

      return newHeights;
    });
  }, [updateNodeInternals]);

  // Restore original callback functions without aggressive auto-close
  const memoizedOnNodeClick = useCallback(onNodeClick, [onNodeClick]);
  const memoizedOnUpdateNode = useCallback(onUpdateNode, [onUpdateNode]);
  const memoizedOnAddChild = useCallback(onAddChild, [onAddChild]);
  const memoizedOnDeleteNode = useCallback(onDeleteNode, [onDeleteNode]);
  const memoizedOnDeletePath = useCallback(onDeletePath, [onDeletePath]);

  // Clear placeholder states when switching to View Mode
  useEffect(() => {
    if (!isEditing) {
      // Clear dashed placeholder nodes
      setShowDashedPlaceholder(null);
      // Close any open node toolbars
      setActiveToolbarNodeId(null);
    }
  }, [isEditing]);

  // Convert automation tree to ReactFlow nodes and edges using V2 system
  const { flowNodes, flowEdges } = useMemo(() => {
    if (!trigger || !trigger.root) {
      return { flowNodes: [], flowEdges: [] };
    }
    const flowNodes: Node<FlowNodeData>[] = [];
    const flowEdges: Edge[] = [];

    // Layout configuration
    const NODE_WIDTH = 280;
    const HORIZONTAL_SPACING = 480;
    const BASE_VERTICAL_SPACING = 45; // Balanced spacing for regular nodes
    const CONDITION_VERTICAL_SPACING = 120; // More generous spacing for condition nodes
    const LEAF_NODE_END_SPACING = 45; // Consistent 32px spacing between leaf nodes and End labels
    const CONDITION_BASE_HEIGHT = 20; // Base height for condition nodes
    const CONDITION_ITEM_HEIGHT = 32; // Height per condition item
    const CONDITION_PADDING = 24; // Padding for condition items
    const ADD_BUTTON_HEIGHT = 32; // Height for "Add Condition" button
    const DESCRIPTION_HEIGHT = 40; // Height for description text
    const TRIGGER_HEIGHT = 180; // Height for trigger nodes

    // Dynamic spacing calculation based on actual measured node heights
    const getVerticalSpacing = (node: AutomationNodeV2): number => {
      // Handle placeholder nodes specifically
      if (node.metadata?.isPlaceholder) {
        // Use measured height if available, otherwise fallback to static height
        const measuredHeight = nodeHeights.get(node.id);
        return (measuredHeight || 200) + BASE_VERTICAL_SPACING;
      }

      // Use measured height if available for consistent 32px spacing
      const measuredHeight = nodeHeights.get(node.id);
      if (measuredHeight) {
        // Return measured height + consistent base spacing for perfect 32px gaps
        return measuredHeight + BASE_VERTICAL_SPACING;
      }

      if (node.type === AutomationNodeType.CONDITION) {
        // Use measured height with generous spacing for condition nodes
        const measuredHeight = nodeHeights.get(node.id);
        if (measuredHeight) {
          return measuredHeight + CONDITION_VERTICAL_SPACING;
        }

        // Fallback to static calculation when height not yet measured
        const conditions = node.metadata?.children || [];
        const singleCondition = node.metadata?.condition;
        const hasConditions = conditions.length > 0 || singleCondition;

        // Calculate actual content height
        let contentHeight = CONDITION_BASE_HEIGHT; // Base card height

        if (hasConditions) {
          // Add height for each condition badge
          const totalConditions = conditions.length + (singleCondition && conditions.length === 0 ? 1 : 0);
          contentHeight += totalConditions * CONDITION_ITEM_HEIGHT;
          contentHeight += (totalConditions - 1) * CONDITION_PADDING; // Spacing between conditions
        } else {
          // No conditions - add height for "No conditions set" badge
          contentHeight += CONDITION_ITEM_HEIGHT;
        }

        // Always add height for "Add Condition" button space (visible or not)
        contentHeight += ADD_BUTTON_HEIGHT + CONDITION_PADDING;

        // Add height for description text
        contentHeight += DESCRIPTION_HEIGHT;

        // Always add height for True/False buttons space at bottom (visible or not)
        contentHeight += 60; // Space for True/False buttons

        // Calculate spacing needed to prevent overlap with compact padding for condition nodes
        const spacingNeeded = contentHeight + BASE_VERTICAL_SPACING + 15;

        return spacingNeeded;
      }

      // For ACTION nodes, calculate height based on content
      if (node.type === AutomationNodeType.ACTION) {
        const isConfigured = node.metadata && (
          (node.subType === AutomationActionNodeSubType.SEND_EMAIL && node.metadata.templateId && node.metadata.resourceId !== undefined && node.metadata.fromPreviousSender !== undefined) ||
          (node.subType === AutomationActionNodeSubType.HANDLE_PFA_BOOST && node.metadata.boostId && node.metadata.resourceId !== undefined && node.metadata.fromPreviousSender !== undefined) ||
          (node.subType === AutomationActionNodeSubType.HANDLE_CASHBACK_BOOST && node.metadata.resourceId !== undefined && node.metadata.fromPreviousSender !== undefined) ||
          (node.subType === AutomationActionNodeSubType.UPDATE_MEMBER_DATA && node.metadata.type && node.metadata.fieldValue) ||
          (node.subType === AutomationActionNodeSubType.ADD_TO_OFFER && node.metadata.offerId && node.metadata.selectedPayoutId)
        );

        let actionHeight = 100;

        if (isConfigured) {
          if (node.subType === AutomationActionNodeSubType.SEND_EMAIL) {
            // SendEmail has: template card + subject line + sender info + padding
            actionHeight += 120;
          } else if (node.subType === AutomationActionNodeSubType.UPDATE_MEMBER_DATA) {
            // UpdateMemberData has: action card + type/value line
            actionHeight += 70;
          } else if (node.subType === AutomationActionNodeSubType.ADD_TO_OFFER) {
            // AddToOffer has: offer ID card + action description + email notification (if enabled)
            actionHeight += 90; // Base height for offer ID card and action description
            if (node.metadata?.sendEmailToNotifyMember) {
              actionHeight += 20; // Extra height for email notification line
            }
          } else {
            actionHeight += 60;
          }
        } else {
          // Not configured - just the "Configure Action" button
          actionHeight += 40;
        }

        return actionHeight + BASE_VERTICAL_SPACING;
      }

      // For DELAY nodes, calculate height based on configuration
      if (node.type === AutomationNodeType.DELAY) {
        const isConfigured = typeof node.metadata?.durationMs === 'number';
        let delayHeight = 80;

        if (isConfigured) {
          delayHeight += 35;
        } else {
          delayHeight += 30;
        }

        return delayHeight + BASE_VERTICAL_SPACING;
      }

      // For BRANCH nodes and other node types, use compact base spacing
      return BASE_VERTICAL_SPACING + 40;
    };

    // Track processed nodes to prevent cycles and bidirectional edges
    const processedNodes = new Set<string>();
    const activeNodes = new Set<string>(); // Track nodes in current path to detect cycles
    const createdEdges = new Set<string>(); // Track created edges to prevent duplicates

    // Helper function to safely add edges without duplicates
    const addEdge = (edge: Edge) => {
      const edgeKey = `${edge.source}->${edge.target}`;
      const reverseKey = `${edge.target}->${edge.source}`;

      // Check if this edge or its reverse already exists
      if (createdEdges.has(edgeKey)) {
        return;
      }

      if (createdEdges.has(reverseKey)) {
        return;
      }

      // Add the edge
      createdEdges.add(edgeKey);
      flowEdges.push(edge);
    };

    // Calculate positions using a hierarchical layout
    const calculateLayout = (node: AutomationNodeV2, x: number, y: number, depth: number): void => {
      // Prevent infinite loops and cycles
      if (activeNodes.has(node.id)) {
        return;
      }

      // Add to active path
      activeNodes.add(node.id);

      // Add extra spacing for the first few nodes to prevent overlap
      const extraSpacing = depth <= 3 ? 50 : 25;

      // Determine the ReactFlow node type - use PLACEHOLDER for placeholder nodes
      const reactFlowNodeType = node.metadata?.isPlaceholder ? 'PLACEHOLDER' : node.type;

      // Add the current node
      flowNodes.push({
        id: node.id,
        type: reactFlowNodeType,
        position: { x, y },
        data: {
          nodeId: node.id,
          nodeType: node.type,
          subType: node.subType,
          metadata: node.metadata,
          isEditing,
          isSelected: node.id === selectedNodeId,
          isRootNode: node.id === trigger.root?.id, // Mark if this is the root node
          onNodeClick: memoizedOnNodeClick,
          onUpdateNode: memoizedOnUpdateNode,
          onAddChild: memoizedOnAddChild,
          onReplacePlaceholder: actions.replacePlaceholder,
          onDeleteNode: memoizedOnDeleteNode,
          onDeletePath: memoizedOnDeletePath,
          onNodeResize: handleNodeResize,
          onShowDashedPlaceholder: (nodeId: string | null, isElseBranch?: boolean, hasOppositeBranch?: boolean, clickedBranch?: 'then' | 'else') => {
            if (nodeId === null) {
              setShowDashedPlaceholder(null);
            } else {
              setShowDashedPlaceholder({ nodeId, isElseBranch, hasOppositeBranch, clickedBranch });
            }
          },
          isToolbarVisible: activeToolbarNodeId === node.id,
          onRequestToolbarOpen: (nodeId: string) => {
            setActiveToolbarNodeId(nodeId);
          },
          onCloseToolbar: () => {
            setActiveToolbarNodeId(null);
          },
          // Pass branch information when this node's toolbar is active
          toolbarBranch: activeToolbarNodeId === node.id && showDashedPlaceholder?.nodeId === node.id ? {
            isElseBranch: showDashedPlaceholder.isElseBranch,
            hasOppositeBranch: showDashedPlaceholder.hasOppositeBranch
          } : undefined,
          automationId,
          templateId,
          actionOptions,
          programData,
          isStaOffer,
        },
        draggable: false,
        selectable: true,
      });

      // Handle children and elseChildren for condition nodes
      if (node.type === AutomationNodeType.CONDITION) {
        const trueChildren = node.children || [];
        const falseChildren = node.elseChildren || [];

        // Position branches to match ConditionNodeV2 layout:
        // Then: CENTER when Else is empty, LEFT when Else has children
        // Else: Always RIGHT
        const hasElseChildren = falseChildren.length > 0;
        const hasThenChildren = trueChildren.length > 0;

        // If we have Then children, treat them as True branch
        if (hasThenChildren) {
          // Use direct calculation for condition nodes to ensure generous spacing
          const measuredHeight = nodeHeights.get(node.id);
          let childY;

          if (measuredHeight) {
            // Use measured height + generous condition spacing (bypassing getVerticalSpacing)
            childY = y + measuredHeight + CONDITION_VERTICAL_SPACING;
          } else {
            // Fallback: use getVerticalSpacing for unmeasured nodes
            const verticalSpacing = getVerticalSpacing(node);
            childY = y + verticalSpacing + extraSpacing;
          }

          trueChildren.forEach((child, index) => {
            // Match ConditionNodeV2 handle positioning exactly:
            // Then position: LEFT (15%) when both Then and Else have children, CENTER (50%) when only Then has children
            const conditionNodeWidth = 342; // From ConditionNodeV2.tsx: w-[342px]
            const hasElseChildren = falseChildren.length > 0;
            const thenPosition = hasElseChildren ? 15 : 50; // LEFT (15%) when Else has children, CENTER (50%) when Else is empty

            // x is the center of the parent node, so we need to offset from center
            // thenPosition is percentage from left edge, so we convert to offset from center
            const thenOffsetFromCenter = (thenPosition - 50) * (conditionNodeWidth / 100); // Convert percentage to pixels from center
            const thenHandleX = x + thenOffsetFromCenter;

            let baseX = thenHandleX;

            // Apply horizontal offset for branching when Else exists
            if (hasElseChildren) {
              // Offset Then children further left to create visual separation
              baseX = thenHandleX - 120; // Move 120px left from the handle for better visual separation
            }

            // If multiple children in then branch, spread them horizontally
            if (trueChildren.length > 1) {
              const totalWidth = (trueChildren.length - 1) * HORIZONTAL_SPACING;
              const startX = baseX - totalWidth / 2;
              baseX = startX + index * HORIZONTAL_SPACING;
            }
            const childX = baseX;
            calculateLayout(child, childX, childY, depth + 1);

            // Use custom edge label only (no default fallback)
            const edgeLabel = child.edgeLabel;

            addEdge({
              id: `${node.id}-true-${child.id}`,
              source: node.id,
              target: child.id,
              sourceHandle: 'true',
              type: 'custom',
              data: {
                label: 'True', // Keep for color coding
                edgeLabel,
                onLabelChange: handleEdgeLabelChange,
                onLabelDelete: handleEdgeLabelDelete,
                onDuplicate: handleEdgeDuplicate,
                onDeleteNode: memoizedOnDeleteNode,
                onDeletePath: memoizedOnDeletePath,
                nodeData: { ...child },
                parentNodeData: node,
                isElseBranch: false,
                isEditingMode: isEditing,
              },
              markerEnd: {
                type: MarkerType.Arrow,
                color: FLOW_COLORS.MARKER.TRUE,
                width: 14,
                height: 14,
              },
              style: {
                stroke: FLOW_COLORS.EDGE.TRUE_BRANCH,
                strokeWidth: 1,
                strokeDasharray: '0',
              },
            });
          });
        }

        // Process Else branch - always position to the RIGHT
        if (hasElseChildren) {
          // Use direct calculation for condition nodes to ensure generous spacing
          const measuredHeight = nodeHeights.get(node.id);
          let childY;

          if (measuredHeight) {
            // Use measured height + generous condition spacing (bypassing getVerticalSpacing)
            childY = y + measuredHeight + CONDITION_VERTICAL_SPACING;
          } else {
            // Fallback: use getVerticalSpacing for unmeasured nodes
            const verticalSpacing = getVerticalSpacing(node);
            childY = y + verticalSpacing + extraSpacing;
          }

          falseChildren.forEach((child, index) => {
            // Match ConditionNodeV2 handle positioning exactly:
            // Else position: Always 85% (RIGHT)
            const conditionNodeWidth = 342; // From ConditionNodeV2.tsx: w-[342px]
            const elsePosition = 85; // Always 85% (RIGHT)
            // x is the center of the parent node, so we need to offset from center
            // elsePosition is percentage from left edge, so we convert to offset from center
            const elseOffsetFromCenter = (elsePosition - 50) * (conditionNodeWidth / 100); // Convert percentage to pixels from center
            const elseHandleX = x + elseOffsetFromCenter;

            let baseX = elseHandleX;

            // Apply horizontal offset for branching - Else children move further right
            baseX = elseHandleX + 120; // Move 120px right from the handle for better visual separation

            // If multiple children in else branch, spread them horizontally
            if (falseChildren.length > 1) {
              const totalWidth = (falseChildren.length - 1) * HORIZONTAL_SPACING;
              const startX = baseX - totalWidth / 2;
              baseX = startX + index * HORIZONTAL_SPACING;
            }
            const childX = baseX;
            calculateLayout(child, childX, childY, depth + 1);

            // Use custom edge label only (no default fallback)
            const edgeLabel = child.edgeLabel;

            addEdge({
              id: `${node.id}-false-${child.id}`,
              source: node.id,
              target: child.id,
              sourceHandle: 'false',
              type: 'custom',
              data: {
                label: 'False', // Keep for color coding
                edgeLabel,
                onLabelChange: handleEdgeLabelChange,
                onLabelDelete: handleEdgeLabelDelete,
                onDuplicate: handleEdgeDuplicate,
                onDeleteNode: memoizedOnDeleteNode,
                onDeletePath: memoizedOnDeletePath,
                nodeData: { ...child },
                parentNodeData: node,
                isElseBranch: true,
                isEditingMode: isEditing,
              },
              markerEnd: {
                type: MarkerType.Arrow, // Outline arrow
                color: FLOW_COLORS.MARKER.FALSE,
                width: 14,
                height: 14,
              },
              style: {
                stroke: FLOW_COLORS.EDGE.FALSE_BRANCH,
                strokeWidth: 1,
                strokeDasharray: '0',
              },
            });
          });
        }
      } else if (node.children && node.children.length > 0) {
        if (node.children.length === 1) {
          // Single child - place directly below
          const childX = x;
          const childY = y + getVerticalSpacing(node) + extraSpacing;
          calculateLayout(node.children[0], childX, childY, depth + 1);

          // Add edge with custom label
          const childNode = node.children[0];
          const edgeLabel = childNode.edgeLabel;

          addEdge({
            id: `${node.id}-${childNode.id}`,
            source: node.id,
            target: childNode.id,
            type: 'custom',
            data: {
              edgeLabel,
              onLabelChange: handleEdgeLabelChange,
              onLabelDelete: handleEdgeLabelDelete,
              onDuplicate: handleEdgeDuplicate,
              onDeleteNode: memoizedOnDeleteNode,
              onDeletePath: memoizedOnDeletePath,
              nodeData: { ...childNode },
              parentNodeData: node,
              isEditingMode: isEditing,
            },
            markerEnd: {
              type: MarkerType.Arrow, // Outline arrow
              color: FLOW_COLORS.MARKER.DEFAULT,
              width: 14,
              height: 14,
            },
            style: {
              stroke: FLOW_COLORS.EDGE.DEFAULT,
              strokeWidth: 1, // Try thinner stroke
              strokeDasharray: '0',
            },
          });
        } else {
          // Multiple children - spread horizontally (branching)
          const totalWidth = (node.children.length - 1) * HORIZONTAL_SPACING;
          const startX = x - totalWidth / 2;
          const childY = y + getVerticalSpacing(node) + extraSpacing;

          node.children.forEach((child, index) => {
            const childX = startX + index * HORIZONTAL_SPACING;
            calculateLayout(child, childX, childY, depth + 1);

            // Use custom edge label only (no default fallback)
            const edgeLabel = child.edgeLabel;

            addEdge({
              id: `${node.id}-${child.id}`,
              source: node.id,
              target: child.id,
              type: 'custom',
              data: {
                edgeLabel,
                onLabelChange: handleEdgeLabelChange,
                onLabelDelete: handleEdgeLabelDelete,
                onDuplicate: handleEdgeDuplicate,
                onDeleteNode: memoizedOnDeleteNode,
                onDeletePath: memoizedOnDeletePath,
                nodeData: { ...child },
                parentNodeData: node,
                isEditingMode: isEditing,
              },
              markerEnd: {
                type: MarkerType.Arrow, // Outline arrow
                color: FLOW_COLORS.MARKER.DEFAULT,
                width: 14,
                height: 14,
              },
              style: {
                stroke: FLOW_COLORS.EDGE.DEFAULT,
                strokeWidth: 1, // Try thinner stroke
              },
            });
          });
        }
      }

      // Remove from active path when done processing this node
      activeNodes.delete(node.id);
      processedNodes.add(node.id);
    };

        // Start with trigger node - center it horizontally
    flowNodes.push({
      id: trigger.id,
      type: 'TRIGGER', // Use TRIGGER type for proper node rendering
      position: { x: -NODE_WIDTH / 2, y: 0 },
      data: {
        nodeId: trigger.id,
        nodeType: 'TRIGGER' as any,
        metadata: { triggerType: trigger.type }, // Pass trigger type in correct format
        isEditing,
        isSelected: trigger.id === selectedNodeId,
        onNodeClick: memoizedOnNodeClick,
        onUpdateNode: memoizedOnUpdateNode,
        onAddChild: memoizedOnAddChild,
        onDeleteNode: memoizedOnDeleteNode,
        onDeletePath: memoizedOnDeletePath,
        onNodeResize: handleNodeResize,
        onShowDashedPlaceholder: (nodeId: string | null, isElseBranch?: boolean, hasOppositeBranch?: boolean, clickedBranch?: 'then' | 'else') => {
          if (nodeId === null) {
            setShowDashedPlaceholder(null);
          } else {
            setShowDashedPlaceholder({ nodeId, isElseBranch, hasOppositeBranch, clickedBranch });
          }
        },
        isToolbarVisible: activeToolbarNodeId === trigger.id,
        onRequestToolbarOpen: (nodeId: string) => {
          setActiveToolbarNodeId(nodeId);
        },
        onCloseToolbar: () => {
          setActiveToolbarNodeId(null);
        },
        // Pass branch information when this node's toolbar is active
        toolbarBranch: activeToolbarNodeId === trigger.id && showDashedPlaceholder?.nodeId === trigger.id ? {
          isElseBranch: showDashedPlaceholder.isElseBranch,
          hasOppositeBranch: showDashedPlaceholder.hasOppositeBranch
        } : undefined,
      },
      draggable: false,
      selectable: true,
    });

    // Always render the root node if it exists, regardless of delay duration
    if (trigger.root) {
      // Always render root as visible delay node - no more hidden roots
      const rootX = -NODE_WIDTH / 2;
      const rootY = TRIGGER_HEIGHT + BASE_VERTICAL_SPACING;

      // Layout the root node and its descendants
      calculateLayout(trigger.root, rootX, rootY, 1);

      // Add edge from trigger to the visible root node
      const edgeLabel = trigger.root.edgeLabel;
      addEdge({
        id: `trigger-${trigger.root.id}`,
        source: trigger.id,
        target: trigger.root.id,
        type: 'custom',
        data: {
          edgeLabel,
          onLabelChange: handleEdgeLabelChange,
          onLabelDelete: handleEdgeLabelDelete,
          onDuplicate: handleEdgeDuplicate,
          onDeleteNode: memoizedOnDeleteNode,
          onDeletePath: memoizedOnDeletePath,
          nodeData: { ...trigger.root },
          parentNodeData: null,
          isEditingMode: isEditing,
        },
        markerEnd: {
          type: MarkerType.Arrow,
          color: FLOW_COLORS.MARKER.DEFAULT,
          width: 16,
          height: 16,
        },
        style: {
          stroke: FLOW_COLORS.EDGE.DEFAULT,
          strokeWidth: 1,
          strokeDasharray: '0',
        },
      });
    }

    // Add "End" indicators for leaf nodes (nodes with no children)
    // NOTE: These are display-only nodes and will NOT be saved to the automation blueprint
    const addEndIndicators = () => {
      // First, remove any existing End nodes to prevent duplicates across renders
      const existingEndNodes = flowNodes.filter((node) => node.type === 'END').map((node) => node.id);
      if (existingEndNodes.length > 0) {
        flowNodes.splice(0, flowNodes.length, ...flowNodes.filter((node) => node.type !== 'END'));
        // Also remove edges to End nodes
        flowEdges.splice(0, flowEdges.length, ...flowEdges.filter((edge) => !existingEndNodes.includes(edge.target)));
      }

      // Find all leaf nodes (nodes that have no children and no elseChildren)
      const leafNodes = flowNodes.filter((node) => {
        if (node.type === 'TRIGGER') return false; // Skip trigger nodes
        if (node.data?.metadata?.isEndNode) return false; // Skip existing end nodes

        // Special handling for Condition nodes - they need End node processing even with partial children
        if (node.type === AutomationNodeType.CONDITION) {
          return true; // Always process Condition nodes for potential End nodes
        }

        // Check if this node has any children by looking at outgoing edges
        const hasOutgoingEdges = flowEdges.some((edge) => edge.source === node.id);
        return !hasOutgoingEdges;
      });

      leafNodes.forEach((leafNode) => {
        // Find the corresponding automation node to calculate dynamic spacing
        const automationNode = leafNode.id === trigger.id ?
          { id: trigger.id, type: 'TRIGGER' as any, children: [], elseChildren: undefined, metadata: {}, variables: [] } :
          findNodeById(trigger.root, leafNode.id);

        // Calculate the actual bottom position of the node based on its visual content height
        const getNodeBottomPosition = (node: AutomationNodeV2, leafNode: any): number => {
          if (!node) return leafNode.position.y + BASE_VERTICAL_SPACING;

          let nodeContentHeight = 0;

          // Handle PlaceholderNodeV2 specifically
          if (leafNode.type === 'PLACEHOLDER') {
            // PlaceholderNodeV2 has min-h-[140px] in collapsed state, ~200px when expanded
            // Use measured height if available, otherwise fallback to collapsed height
            const measuredHeight = nodeHeights.get(leafNode.id);
            nodeContentHeight = measuredHeight || 140; // Consistent with min-h-[140px] from PlaceholderNodeV2
            return leafNode.position.y + nodeContentHeight;
          }

          if (node.type === AutomationNodeType.CONDITION) {
            // Base condition node height
            nodeContentHeight = 80; // Header + padding

            // Add height for condition badges
            const conditions = node.metadata?.children || [];
            const singleCondition = node.metadata?.condition;
            const hasConditions = conditions.length > 0 || singleCondition;

            if (hasConditions) {
              const totalConditions = conditions.length + (singleCondition && conditions.length === 0 ? 1 : 0);
              nodeContentHeight += totalConditions * 32;
              nodeContentHeight += Math.max(0, totalConditions - 1) * 8; // Spacing between badges
            } else {
              nodeContentHeight += 32;
            }

            // Always add height for buttons space (visible or not)
            nodeContentHeight += 40; // Add Condition button space
            nodeContentHeight += 60; // True/False buttons space

            // Add description height
            nodeContentHeight += 20;
          } else if (node.type === AutomationNodeType.ACTION) {
            // Base action node height
            nodeContentHeight = 60; // Header + padding

            const isConfigured = node.metadata && (
              (node.subType === AutomationActionNodeSubType.SEND_EMAIL && node.metadata.templateId) ||
              (node.subType === AutomationActionNodeSubType.HANDLE_PFA_BOOST && node.metadata.boostId) ||
              (node.subType === AutomationActionNodeSubType.HANDLE_CASHBACK_BOOST && node.metadata.resourceId !== undefined) ||
              (node.subType === AutomationActionNodeSubType.UPDATE_MEMBER_DATA && node.metadata.type) ||
              (node.subType === AutomationActionNodeSubType.ADD_TO_OFFER && node.metadata.offerId)
            );

            if (isConfigured) {
              if (node.subType === AutomationActionNodeSubType.SEND_EMAIL) {
                // Email template card + subject + sender info
                nodeContentHeight += 120;
              } else if (node.subType === AutomationActionNodeSubType.UPDATE_MEMBER_DATA) {
                // Handle multiple tags - each tag adds height
                if (node.metadata?.values && Array.isArray(node.metadata.values)) {
                  const numTags = node.metadata.values.length;
                  nodeContentHeight += 40 + (numTags * 24); // Base + tag badges
                } else {
                  nodeContentHeight += 60;
                }
              } else if (node.subType === AutomationActionNodeSubType.ADD_TO_OFFER) {
                nodeContentHeight += 90;
                if (node.metadata?.sendEmailToNotifyMember) {
                  nodeContentHeight += 20;
                }
              } else {
                nodeContentHeight += 60;
              }
            } else {
              nodeContentHeight += 40; // Configure button
            }
          } else if (node.type === AutomationNodeType.DELAY) {
            nodeContentHeight = 80; // Base height

            const isConfigured = typeof node.metadata?.durationMs === 'number';
            if (isConfigured) {
              nodeContentHeight += 35; // Configured delay display
            } else {
              nodeContentHeight += 30; // "Add Delay Time" button
            }
          } else {
            // Default for other node types
            nodeContentHeight = 80;
          }

          return leafNode.position.y + nodeContentHeight;
        };

        // Calculate End position using measured height when available, fallback to static calculation
        let endY;
        const measuredHeight = nodeHeights.get(leafNode.id);
        const isConditionNode = leafNode.type === AutomationNodeType.CONDITION;

        if (measuredHeight && !isConditionNode) {
          // Use measured height for non-condition nodes with consistent 32px spacing
          const nodeBottomY = leafNode.position.y + measuredHeight;
          endY = nodeBottomY + LEAF_NODE_END_SPACING;
        } else {
          // Fallback to static calculation for condition nodes or when height not measured
          const nodeBottomY = getNodeBottomPosition(automationNode, leafNode);
          const CONSISTENT_END_SPACING = isConditionNode ? 40 : 32; // 40px for Condition→End to match branching height, 32px for others
          endY = nodeBottomY + CONSISTENT_END_SPACING;
        }

        // Single End node handling for Condition nodes - only Then branch gets top-level End when empty
        if (leafNode.type === AutomationNodeType.CONDITION && automationNode) {
          // Check for Then branch - must have actual children (not just placeholders)
          // A placeholder edge goes to a node with '-placeholder' in its ID
          const hasThenChild = (automationNode.children && automationNode.children.length > 0) ||
            flowEdges.some((edge) =>
              edge.source === leafNode.id &&
              edge.sourceHandle === 'true' &&
              !edge.target.includes('-end') && // Not an End node
              !edge.target.includes('-placeholder')); // Not a placeholder

          // Check for Else branch using both automation data AND edges (to catch placeholders)
          const hasElseChild = (automationNode.elseChildren && automationNode.elseChildren.length > 0) ||
            flowEdges.some((edge) =>
              edge.source === leafNode.id &&
              edge.sourceHandle === 'false' &&
              !edge.target.includes('-end'));

          // Create End node for Then branch when Then is empty (regardless of Else status)
          // Else branch never gets a top-level End node
          if (!hasThenChild) {
            // Updated logic: LEFT (15%) when Else has children, CENTER (50%) when Else is empty
            const conditionNodeWidth = 342; // From ConditionNodeV2.tsx: w-[342px]
            const thenPosition = hasElseChild ? 15 : 50; // LEFT (15%) when Else has children, CENTER (50%) when Else is empty

            // Position End node for branched connector when Else exists
            // leafNode.position.x is the left edge of the condition node in ReactFlow coordinate system
            // thenPosition is percentage from left edge, so we add the percentage offset
            const thenHandleX = leafNode.position.x + (conditionNodeWidth * (thenPosition / 100));

            let thenEndX;
            if (hasElseChild) {
              // When Else exists, position End node to the left of the Then handle for branching effect
              // This creates horizontal distance needed for the branched connector
              thenEndX = thenHandleX - 120 - 19; // Move End node left from handle (120px offset + centering)
            } else {
              // When no Else, center End node directly under Then handle (straight line)
              thenEndX = thenHandleX - 19; // Center the End node under the handle (End node is 38px wide)
            }

            // Create single End node for Then branch only
            const thenEndNodeId = `${leafNode.id}-then-end`;

            flowNodes.push({
              id: thenEndNodeId,
              type: 'END',
              position: { x: thenEndX, y: endY },
              data: {
                nodeId: thenEndNodeId,
                nodeType: 'END' as any,
                metadata: { isEndNode: true, branchType: 'then-only' },
                isEditing: false,
                isSelected: false,
              },
              draggable: false,
              selectable: false,
            } as any);

            // Add edge from Then handle to Then End node
            addEdge({
              id: `${leafNode.id}-then-to-end`,
              source: leafNode.id,
              sourceHandle: 'true',
              target: thenEndNodeId,
              type: 'custom', // Use custom edge for branching when Else exists
              data: {
                edgeLabel: null,
                nodeData: null,
                parentNodeData: null,
                isEditingMode: isEditing,
              },
              style: {
                stroke: FLOW_COLORS.EDGE.TRUE_BRANCH,
                strokeWidth: 1,
                strokeDasharray: '0',
              },
              markerEnd: {
                type: MarkerType.Arrow,
                color: FLOW_COLORS.MARKER.TRUE,
                width: 16,
                height: 16,
              },
            });
          }

          return; // Skip the default single End node creation for this Condition node
        }

        // Create single End node for leaf nodes (Action, Delay, Placeholder)
        const endNodeId = `${leafNode.id}-end`;

        // Calculate the center position for the "End" node
        // Most nodes have a width of 342px, center the End node perfectly
        const ACTUAL_NODE_WIDTH = 342; // Most automation nodes are 342px wide
        const centeredEndX = leafNode.position.x + (ACTUAL_NODE_WIDTH / 2) - 19;

        // Add "End" node
        flowNodes.push({
          id: endNodeId,
          type: 'END', // Use custom END node type
          position: { x: centeredEndX, y: endY }, // Center "End" exactly below the leaf node
          data: {
            nodeId: endNodeId,
            nodeType: 'END' as any,
            metadata: { isEndNode: true },
            isEditing: false,
            isSelected: false,
          },
          draggable: false,
          selectable: false,
        } as any);

        // Add edge from leaf node to "End"
        addEdge({
          id: `${leafNode.id}-to-end`,
          source: leafNode.id,
          target: endNodeId,
          type: 'custom',
          data: {
            edgeLabel: null, // No label for end nodes
            // No handlers - end node edges shouldn't be editable
            nodeData: null,
            parentNodeData: null,
            isEditingMode: isEditing,
          },
          style: {
            stroke: FLOW_COLORS.EDGE.END_NODE,
            strokeWidth: 1,
            strokeDasharray: '0',
          },
          markerEnd: {
            type: MarkerType.Arrow, // Outline arrow for End nodes
            color: FLOW_COLORS.MARKER.END_NODE,
            width: 16,
            height: 16,
          },
        });
      });
    };

    addEndIndicators();

    // Add dashed placeholder when NodeToolbar is open
    if (showDashedPlaceholder) {
      const parentNodeId = showDashedPlaceholder.nodeId;
      const parentNode = flowNodes.find((node) => node.id === parentNodeId);

      if (parentNode) {
        // Find the automation node to get proper spacing
        const automationNode = findNodeById(trigger.root, parentNodeId);
        const parentVerticalSpacing = automationNode ? getVerticalSpacing(automationNode) : 150;

        // Handle CONDITION nodes with proper branch positioning
        const parentIsCondition = parentNode.type === AutomationNodeType.CONDITION;
        const desiredHandle = showDashedPlaceholder?.isElseBranch ? 'false' : 'true';

        let placeholderY = parentNode.position.y + parentVerticalSpacing;
        let placeholderX = parentNode.position.x;

        // Find all child edges to detect multi-branch scenarios
        const allChildEdges = flowEdges.filter((edge) =>
          edge.source === parentNodeId && !edge.target.includes('-end')
        );
        const isMultiBranch = allChildEdges.length >= 2;

        // For condition nodes, check if the specific branch has multiple children
        let isConditionBranchMulti = false;
        if (parentIsCondition) {
          const branchEdges = allChildEdges.filter((edge) =>
            edge.sourceHandle === desiredHandle
          );
          isConditionBranchMulti = branchEdges.length >= 2;
        }

        // For condition nodes, calculate proper branch positioning
        if (parentIsCondition && !isConditionBranchMulti) {
          const conditionNodeWidth = 342;
          // Check if Else has children to determine Then position
          const automationNode = findNodeById(trigger.root, parentNodeId);
          // Check for Else branch using both automation data AND edges (to catch placeholders)
          const hasElseChild = (automationNode?.elseChildren && automationNode.elseChildren.length > 0) ||
            flowEdges.some((edge) =>
              edge.source === parentNodeId &&
              edge.sourceHandle === 'false' &&
              !edge.target.includes('-end'));

          // Add extra spacing to account for branch labels and make it feel part of branch flow
          const branchLabelSpacing = 40; // Additional spacing for branch label integration
          placeholderY = parentNode.position.y + parentVerticalSpacing + branchLabelSpacing;

          if (showDashedPlaceholder?.isElseBranch) {
            // Else branch - always positioned at RIGHT (85%)
            const elseHandleX = parentNode.position.x + (conditionNodeWidth * 0.85);
            placeholderX = elseHandleX - 171; // Center placeholder under Else handle
          } else {
            // Then branch - position based on whether Else has children
            // CENTER (50%) when Else is empty, LEFT (15%) when Else has children
            const thenPositionPercent = hasElseChild ? 0.15 : 0.5;
            const thenHandleX = parentNode.position.x + (conditionNodeWidth * thenPositionPercent);
            placeholderX = thenHandleX - 171; // Center placeholder under Then handle
          }
        }

        // Find existing child edge for the desired branch
        let childEdge = flowEdges.find((edge) =>
          edge.source === parentNodeId &&
          (!parentIsCondition || edge.sourceHandle === desiredHandle)
        );
        if (!childEdge && !parentIsCondition) {
          // For non-condition nodes, fall back to any edge
          childEdge = flowEdges.find((edge) => edge.source === parentNodeId);
        }

        // Helper function to move node and all its descendants
        const moveNodeAndDescendants = (nodeId: string, offset: number) => {
          const nodeToMove = flowNodes.find((node) => node.id === nodeId);
          if (nodeToMove) {
            nodeToMove.position.y += offset;
          }

          flowEdges
            .filter((edge) => edge.source === nodeId)
            .forEach((edge) => {
              moveNodeAndDescendants(edge.target, offset);
            });
        };

        if (isConditionBranchMulti || (isMultiBranch && !parentIsCondition)) {
          // Multi-branch scenario: Position placeholder at top of branch group
          // For condition branches, only consider children of the specific branch
          const relevantEdges = isConditionBranchMulti
            ? allChildEdges.filter((edge) => edge.sourceHandle === desiredHandle)
            : allChildEdges;
          const childNodes = relevantEdges
            .map((edge) => flowNodes.find((node) => node.id === edge.target))
            .filter(Boolean);

          if (childNodes.length > 0) {
            // Find the topmost child position (where branches start)
            const topmostChildY = Math.min(...childNodes.map((node) => node!.position.y));

            // Position placeholder above the topmost branch with proper spacing
            const placeholderSpacing = 80; // Space for placeholder + margins
            placeholderY = topmostChildY - placeholderSpacing;

            // For condition multi-branch, use branch-specific X positioning
            if (isConditionBranchMulti) {
              const conditionNodeWidth = 342;
              const automationNode = findNodeById(trigger.root, parentNodeId);
              const hasElseChild = (automationNode?.elseChildren && automationNode.elseChildren.length > 0) ||
                flowEdges.some((edge) =>
                  edge.source === parentNodeId &&
                  edge.sourceHandle === 'false' &&
                  !edge.target.includes('-end'));

              if (showDashedPlaceholder?.isElseBranch) {
                // Else branch - always positioned at RIGHT (85%)
                const elseHandleX = parentNode.position.x + (conditionNodeWidth * 0.85);
                placeholderX = elseHandleX - 171; // Center placeholder under Else handle
              } else {
                // Then branch - position based on whether Else has children
                // CENTER (50%) when Else is empty, LEFT (15%) when Else has children
                const thenPositionPercent = hasElseChild ? 0.15 : 0.5;
                const thenHandleX = parentNode.position.x + (conditionNodeWidth * thenPositionPercent);
                placeholderX = thenHandleX - 171; // Center placeholder under Then handle
              }
            } else {
              placeholderX = parentNode.position.x; // Center with parent for non-condition multi-branch
            }

            // Shift all child branches down to make room for placeholder
            const shiftAmount = placeholderSpacing;
            childNodes.forEach((childNode) => {
              moveNodeAndDescendants(childNode!.id, shiftAmount);
            });
          } else {
            // Fallback if no child nodes found
            placeholderY = parentNode.position.y + parentVerticalSpacing + 30;

            // For condition multi-branch, use branch-specific X positioning
            if (isConditionBranchMulti) {
              const conditionNodeWidth = 342;
              const automationNode = findNodeById(trigger.root, parentNodeId);
              const hasElseChild = (automationNode?.elseChildren && automationNode.elseChildren.length > 0) ||
                flowEdges.some((edge) =>
                  edge.source === parentNodeId &&
                  edge.sourceHandle === 'false' &&
                  !edge.target.includes('-end'));

              if (showDashedPlaceholder?.isElseBranch) {
                // Else branch - always positioned at RIGHT (85%)
                const elseHandleX = parentNode.position.x + (conditionNodeWidth * 0.85);
                placeholderX = elseHandleX - 171; // Center placeholder under Else handle
              } else {
                // Then branch - position based on whether Else has children
                // CENTER (50%) when Else is empty, LEFT (15%) when Else has children
                const thenPositionPercent = hasElseChild ? 0.15 : 0.5;
                const thenHandleX = parentNode.position.x + (conditionNodeWidth * thenPositionPercent);
                placeholderX = thenHandleX - 171; // Center placeholder under Then handle
              }
            } else {
              placeholderX = parentNode.position.x; // Center with parent for non-condition multi-branch
            }
          }
        } else if (childEdge) {
          // Single-branch or condition node: Use existing logic
          const childNode = flowNodes.find((node) => node.id === childEdge.target);
          if (childNode) {
            // Calculate actual parent node bottom using vertical spacing
            const parentBottom = parentNode.position.y + parentVerticalSpacing;
            const childTop = childNode.position.y;
            const availableSpace = childTop - parentBottom;

            // Position placeholder with proper spacing (simplified card design)
            const placeholderHeight = 50; // Height of card placeholder (h-8 + handles + padding)
            const minSpacing = 30; // Equal spacing above and below placeholder

            if (availableSpace >= placeholderHeight + (minSpacing * 2)) {
              // Enough space exists - place in exact middle with equal spacing
              placeholderY = parentBottom + (availableSpace - placeholderHeight) / 2;
            } else {
              // Not enough space - push child node down to create equal spacing
              const requiredSpace = placeholderHeight + (minSpacing * 2);
              const pushDistance = requiredSpace - availableSpace;

              moveNodeAndDescendants(childNode.id, pushDistance);
              placeholderY = parentBottom + minSpacing;
            }

            // Keep the calculated branch position for condition nodes
            if (!parentIsCondition) {
              placeholderX = parentNode.position.x;
            }
          }
        } else {
          // No child exists - position placeholder below parent with proper spacing
          placeholderY = parentNode.position.y + parentVerticalSpacing + 30; // Add extra padding for placeholder
          placeholderX = parentNode.position.x;
        }

        // Add the dashed placeholder node
        const placeholderId = `${parentNodeId}-placeholder`;
        flowNodes.push({
          id: placeholderId,
          type: 'DASHED_PLACEHOLDER',
          position: { x: placeholderX, y: placeholderY },
          data: {
            nodeId: placeholderId,
            nodeType: 'DASHED_PLACEHOLDER' as any,
            metadata: { isPlaceholder: true },
            isVisible: true,
          },
          draggable: false,
          selectable: false,
        });

        // Handle edge creation based on scenario type
        if (isConditionBranchMulti || (isMultiBranch && !parentIsCondition)) {
          // Multi-branch scenario: Only create parent → placeholder edge
          // Keep all existing branch edges untouched
          // No End node for multi-path scenarios (path > 1)

          // Determine placeholder edge color based on branch type
          const placeholderEdgeColor = (parentIsCondition || isConditionBranchMulti)
            ? (showDashedPlaceholder?.isElseBranch
              ? FLOW_COLORS.EDGE.FALSE_BRANCH
              : FLOW_COLORS.EDGE.TRUE_BRANCH)
            : '#e5e7eb';

          flowEdges.push({
            id: `${parentNodeId}-to-placeholder`,
            source: parentNodeId,
            target: placeholderId,
            sourceHandle: (parentIsCondition || isConditionBranchMulti) ? (showDashedPlaceholder?.isElseBranch ? 'false' : 'true') : undefined,
            type: 'custom',
            style: {
              stroke: placeholderEdgeColor,
              strokeWidth: 1,
              strokeDasharray: '5,5',
            },
          });
        } else if (childEdge) {
          // Single-branch or condition node: Use existing logic with edge replacement
          const childNode = flowNodes.find((node) => node.id === childEdge.target);
          if (childNode) {
            // Remove original edge from parent to child
            const originalEdgeIndex = flowEdges.findIndex((edge) =>
              edge.source === parentNodeId && edge.target === childEdge.target
            );
            if (originalEdgeIndex !== -1) {
              flowEdges.splice(originalEdgeIndex, 1);
            }

            // Determine placeholder edge color based on branch type
            const placeholderEdgeColor = parentIsCondition
              ? (showDashedPlaceholder?.isElseBranch
                ? FLOW_COLORS.EDGE.FALSE_BRANCH
                : FLOW_COLORS.EDGE.TRUE_BRANCH)
              : '#e5e7eb';

            // Add edge from parent to placeholder
            flowEdges.push({
              id: `${parentNodeId}-to-placeholder`,
              source: parentNodeId,
              target: placeholderId,
              // Only set handle for CONDITION nodes
              sourceHandle: parentIsCondition ? (showDashedPlaceholder?.isElseBranch ? 'false' : 'true') : undefined,
              type: 'custom',
              style: {
                stroke: placeholderEdgeColor,
                strokeWidth: 1,
                strokeDasharray: '5,5',
              },
            });

            // Add edge from placeholder to child
            flowEdges.push({
              id: `${placeholderId}-to-${childEdge.target}`,
              source: placeholderId,
              target: childEdge.target,
              type: 'custom',
              style: {
                stroke: placeholderEdgeColor,
                strokeWidth: 1,
                strokeDasharray: '5,5',
              },
            });
          }
        } else {
          // Determine placeholder edge color based on branch type
          const placeholderEdgeColor = parentIsCondition
            ? (showDashedPlaceholder?.isElseBranch
              ? FLOW_COLORS.EDGE.FALSE_BRANCH
              : FLOW_COLORS.EDGE.TRUE_BRANCH)
            : '#e5e7eb';

          // Just add edge from parent to placeholder
          flowEdges.push({
            id: `${parentNodeId}-to-placeholder`,
            source: parentNodeId,
            target: placeholderId,
            sourceHandle: parentIsCondition ? (showDashedPlaceholder?.isElseBranch ? 'false' : 'true') : undefined,
            type: 'custom',
            style: {
              stroke: placeholderEdgeColor,
              strokeWidth: 1,
              strokeDasharray: '5,5',
            },
          });

          // Add an End node after the placeholder for proper termination
          const placeholderEndId = `${placeholderId}-end`;
          const placeholderEndY = placeholderY + 100; // Position End node below placeholder
          const placeholderEndX = placeholderX + 171 - 19; // Center End node under placeholder center (171px is half of placeholder width)

          flowNodes.push({
            id: placeholderEndId,
            type: 'END',
            position: { x: placeholderEndX, y: placeholderEndY },
            data: {
              nodeId: placeholderEndId,
              nodeType: 'END' as any,
              metadata: { isEndNode: true, isPlaceholderEnd: true },
              isEditing: false,
              isSelected: false,
              },
            draggable: false,
            selectable: false,
          } as any);

          // Add edge from placeholder to End
          flowEdges.push({
            id: `${placeholderId}-to-end`,
            source: placeholderId,
            target: placeholderEndId,
            type: 'custom',
            style: {
              stroke: placeholderEdgeColor,
              strokeWidth: 1,
              strokeDasharray: '5,5',
            },
          });
        }
      }
    }

    return { flowNodes, flowEdges };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, isEditing, selectedNodeId, edgeUpdateTrigger, handleEdgeLabelChange, showDashedPlaceholder?.nodeId, showDashedPlaceholder?.isElseBranch, layoutUpdateTrigger, nodeHeights]);

  // Update nodes and edges when trigger changes
  useEffect(() => {
    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [flowNodes, flowEdges, setNodes, setEdges]);

  // Initial view centering - only happens once on page load
  useEffect(() => {
    if (flowNodes.length > 0 && !hasInitializedView.current) {
      hasInitializedView.current = true;
      setTimeout(() => {
        fitView({
          padding: 0.15,
          includeHiddenNodes: false,
          duration: 800,
        });
      }, 200); // Give nodes time to render
    }
  }, [flowNodes.length, fitView]);

  // No automatic zoom on subsequent changes - zoom only happens with user intervention
  // Users can manually zoom using React Flow's built-in controls

  // Handle node clicks
  const handleNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    event.stopPropagation();
    onNodeClick(node.id);
  }, [onNodeClick]);

  // Handle canvas click (deselect)
  const handlePaneClick = useCallback(() => {
    onNodeClick(null); // Always deselect when clicking on canvas
    setShowDashedPlaceholder(null); // Hide dashed placeholder
    // Close all open toolbars by clearing the active toolbar
    setActiveToolbarNodeId(null);
  }, [onNodeClick]);

  // Handle drag over
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  // Handle drop
  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();

    try {
      const templateData = event.dataTransfer.getData('application/json');
      const template: NodeTemplate = JSON.parse(templateData);

      // For now, add to the root node - you could enhance this to detect the target node
      const newNode: AutomationNodeV2 = {
        id: generateNodeId(),
        type: template.type,
        subType: template.subType,
        children: [],
        metadata: { ...template.defaultMetadata },
        variables: [],
      };

      // Add to the trigger - this will add as a child to the hidden root node
      actions.addNode(trigger.id, newNode);
    } catch (error) {
      logger.error('Error parsing dropped data:', error);
    }
  }, [actions, trigger.id]);

  // Smart auto-close system: Close toolbar on genuine "blur" events
  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!activeToolbarNodeId) return;

      const target = event.target as Element;

      // Check if click is on toolbar-related elements
      const isClickOnToolbar = target.closest('[data-toolbar]');
      const isClickOnPlusButton = target.closest('[data-plus-button]');
      const isClickOnNodeAction = target.closest('[data-node-action]');
      const isClickOnPopover = target.closest('[role="dialog"], .popover, [data-radix-popper-content-wrapper]');

      // Close toolbar only if clicking outside relevant UI elements
      if (!isClickOnToolbar && !isClickOnPlusButton && !isClickOnNodeAction && !isClickOnPopover) {
        setActiveToolbarNodeId(null);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && activeToolbarNodeId) {
        setActiveToolbarNodeId(null);
      }
    };

    if (activeToolbarNodeId) {
      // Use capture phase to handle events before they bubble
      document.addEventListener('click', handleDocumentClick, true);
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('click', handleDocumentClick, true);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [activeToolbarNodeId]);

  const proOptions = { hideAttribution: true };

  return (
    <div
      className="h-full w-full relative"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        onPaneClick={handlePaneClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={isEditing}
        selectNodesOnDrag={false}
        proOptions={proOptions}
        panOnDrag
        zoomOnScroll
        fitView
        zoomOnPinch
        className={FLOW_COLORS.BACKGROUND.CANVAS_CLASS}
        minZoom={0.3}
        maxZoom={1.8}
      >
        <Background
          color={FLOW_COLORS.BACKGROUND.GRID}
          gap={16}
          size={1}
          style={{ opacity: isEditing ? 0.5 : 0.2 }}
        />
        <Controls
          showInteractive={false}
          className="bg-white border border-grey-2 rounded-lg shadow-sm"
        />
      </ReactFlow>

    </div>
  );
};
